import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { CreateDialogRequiredBlock } from 'components/create-dialog/CreateDialogRequired';
import { CreateDialogSection, CreateDialogSections } from 'components/create-dialog/CreateDialogSection';
import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import { Field, FieldError, FieldLabel } from 'components/ui/field';
import { Input } from 'components/ui/input';
import { ComponentProps } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import MarketingProfileContactsTable from './MarketingProfileContactsTable';

const NAME_MAX_LENGTH = 255;
const NAME_INPUT_ID = 'marketing-profile-name';

type Props = ComponentProps<typeof MarketingProfileContactsTable> & {
    name: string;
    onNameChange: (name: string) => void;
    /** Why the last save was refused, shown under the name. */
    error: string | null;
    isSaving: boolean;
    /** Off while the profile is loading or failed to load, which its page shows in their place. */
    showContacts: boolean;
    /** Whether the table holds a criterion yet. Read by the create dialog only, which counts what is left to fill. */
    hasCriteria: boolean;
};

/**
 * What a marketing profile is made of: its name, and the filter criteria that pick its contacts, set in the table
 * that previews them.
 *
 * The profile's page draws the two one under the other. The create dialog draws both in its Required section,
 * since a profile is saved with neither missing: the name as a field it counts, the table as a block on a row of
 * its own, which it counts as one field, left to fill until a criterion is picked. The section they are declared
 * in is left with nothing, so the dialog does not list it.
 */
export function MarketingProfileBody({
    name,
    onNameChange,
    error,
    isSaving,
    showContacts,
    hasCriteria,
    initialValue,
    profileId,
    onStateChange,
}: Props) {
    const inCreateDialog = useInCreateDialog();
    const input = (
        <Input
            id={NAME_INPUT_ID}
            // `InputWrapper` spaces its control from the label itself, and the dialog is on the paper colour.
            className={inCreateDialog ? undefined : 'bg-paper mt-2'}
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            required
            disabled={isSaving}
            aria-invalid={Boolean(error)}
            maxLength={NAME_MAX_LENGTH}
        />
    );
    const contacts = showContacts ? (
        <MarketingProfileContactsTable
            initialValue={initialValue}
            profileId={profileId}
            onStateChange={onStateChange}
        />
    ) : null;

    if (inCreateDialog) {
        return (
            <CreateDialogSections>
                <CreateDialogSection title="marketing-profiles.profile">
                    <InputWrapper
                        name={NAME_INPUT_ID}
                        label="marketing-profiles.profile-name"
                        required
                        isEmpty={name.trim() === ''}
                        hasErrors={Boolean(error)}
                        error={error ? { message: error } : undefined}
                    >
                        {input}
                    </InputWrapper>
                    <CreateDialogRequiredBlock when isEmpty={!hasCriteria}>
                        {contacts}
                    </CreateDialogRequiredBlock>
                </CreateDialogSection>
            </CreateDialogSections>
        );
    }

    return (
        <>
            <Field className="max-w-[400px] gap-0" data-invalid={Boolean(error) || undefined}>
                <FieldLabel
                    htmlFor={NAME_INPUT_ID}
                    className={cn('text-fg-secondary gap-0 font-normal', error && 'text-destructive')}
                >
                    {t('marketing-profiles.profile-name')}
                    <span aria-hidden className="text-destructive">
                        &thinsp;*
                    </span>
                </FieldLabel>
                {input}
                {error ? <FieldError className="mt-1">{error}</FieldError> : null}
            </Field>
            {contacts}
        </>
    );
}
